import React, { useEffect, useRef, useState } from "react";
import {
  FolderOpen, UploadCloud, Download, Trash2, Loader2, FileText, Cloud, CloudOff,
  Plus, Pencil, X, Settings, ShieldCheck, Search,
} from "lucide-react";
import api from "../../lib/api";
import { PageTitle, Card, Loading, fmtDate } from "./ui";
import { useAuth } from "../../context/AuthContext";

function bytes(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function FolderModal({ initial, onClose, onSaved }) {
  const [f, setF] = useState({ purpose: "", folder_name: "", drive_folder_id: "", description: "", ...initial });
  const [saving, setSaving] = useState(false);
  const up = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const save = async () => {
    setSaving(true);
    try {
      if (initial?.id) await api.put(`/docs/folders/${initial.id}`, f);
      else await api.post("/docs/folders", f);
      onSaved();
    } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-sapphire-deep/40 p-4 backdrop-blur-sm" data-testid="folder-modal">
      <div className="w-full max-w-lg rounded-2xl bg-white p-7 shadow-heritage">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-2xl font-semibold text-sapphire-deep">{initial?.id ? "Edit" : "New"} Folder Assignment</h3>
          <button onClick={onClose} className="text-ink/50 hover:text-garnet"><X size={22} /></button>
        </div>
        <div className="mt-5 space-y-4">
          <div>
            <label className="mb-1 block font-sans text-xs font-semibold uppercase tracking-widest text-stonegrey">Purpose / Process type</label>
            <input className="input-lodge" placeholder="e.g. Meeting Minutes" value={f.purpose} onChange={up("purpose")} data-testid="folder-purpose-input" />
          </div>
          <div>
            <label className="mb-1 block font-sans text-xs font-semibold uppercase tracking-widest text-stonegrey">Google Drive folder name</label>
            <input className="input-lodge" placeholder="e.g. Lodge Minutes" value={f.folder_name} onChange={up("folder_name")} data-testid="folder-name-input" />
          </div>
          <div>
            <label className="mb-1 block font-sans text-xs font-semibold uppercase tracking-widest text-stonegrey">Drive folder ID (optional until connected)</label>
            <input className="input-lodge" placeholder="1AbC… (from the Drive folder URL)" value={f.drive_folder_id} onChange={up("drive_folder_id")} />
          </div>
          <textarea className="input-lodge resize-none" rows={2} placeholder="Description" value={f.description} onChange={up("description")} />
        </div>
        <div className="mt-6 flex justify-end gap-3">
          <button onClick={onClose} className="btn-dark">Cancel</button>
          <button onClick={save} disabled={saving || !f.purpose || !f.folder_name} className="btn-sapphire" data-testid="folder-save-btn">
            {saving ? <Loader2 size={15} className="animate-spin" /> : null} Save
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Documents() {
  const { user } = useAuth();
  const isAdmin = user?.is_admin || ["Treasurer", "Worshipful Master"].includes(user?.rank);
  const [folders, setFolders] = useState(null);
  const [docs, setDocs] = useState([]);
  const [status, setStatus] = useState(null);
  const [purpose, setPurpose] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [folderModal, setFolderModal] = useState(null);
  const [search, setSearch] = useState("");
  const [folderFilter, setFolderFilter] = useState("all");
  const fileRef = useRef();

  const loadAll = () => {
    api.get("/docs/folders").then((r) => { setFolders(r.data); if (!purpose && r.data[0]) setPurpose(r.data[0].purpose); }).catch(() => setFolders([]));
    api.get("/docs").then((r) => setDocs(r.data)).catch(() => setDocs([]));
    api.get("/docs/drive/status").then((r) => setStatus(r.data)).catch(() => {});
  };
  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, []);

  const upload = async () => {
    if (!file || !purpose) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("purpose", purpose);
      fd.append("file", file);
      await api.post("/docs/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      loadAll();
    } finally { setUploading(false); }
  };

  const download = async (d) => {
    const res = await api.get(`/docs/${d.id}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = d.filename; a.click();
    URL.revokeObjectURL(url);
  };

  const del = async (d) => {
    if (!window.confirm(`Delete "${d.filename}"?`)) return;
    await api.delete(`/docs/${d.id}`);
    loadAll();
  };

  const delFolder = async (f) => {
    if (!window.confirm(`Delete folder assignment "${f.purpose}"?`)) return;
    await api.delete(`/docs/folders/${f.id}`);
    loadAll();
  };

  if (!folders) return <Loading />;

  return (
    <div data-testid="documents-page">
      <PageTitle eyebrow="Google Workspace · Drive" title="Documents"
        sub="Attachments are filed into the Lodge's Google Drive folders by purpose." />

      {/* Drive status banner */}
      {status && (
        <div className={`mb-6 flex items-start gap-3 rounded-2xl border p-4 ${status.connected ? "border-emerald/30 bg-emerald/5" : "border-gold/40 bg-gold/10"}`} data-testid="drive-status">
          {status.connected ? <Cloud size={20} className="mt-0.5 text-emerald" /> : <CloudOff size={20} className="mt-0.5 text-gold-deep" />}
          <div className="flex-1">
            <p className="font-sans text-sm font-semibold text-sapphire-deep">
              {status.connected ? "Google Drive connected" : "Google Drive — Mock Mode"}
            </p>
            <p className="font-sans text-sm text-ink/70">{status.message}</p>
          </div>
          {isAdmin && !status.connected && (
            <button disabled title="Provide the Workspace OAuth client to enable"
              className="cursor-not-allowed rounded-full border border-sapphire-deep/20 px-4 py-2 font-sans text-xs font-semibold uppercase tracking-widest text-ink/40"
              data-testid="connect-drive-btn">
              Connect Drive (pending setup)
            </button>
          )}
        </div>
      )}

      {/* Upload */}
      <Card className="mb-8">
        <div className="flex items-center gap-2 text-gold-deep">
          <UploadCloud size={18} /><span className="font-sans text-xs font-semibold uppercase tracking-widest">Upload an attachment</span>
        </div>
        <div className="mt-4 grid gap-4 md:grid-cols-[1fr_1.5fr_auto] md:items-end">
          <div>
            <label className="mb-1 block font-sans text-xs font-semibold uppercase tracking-widest text-stonegrey">Filed under</label>
            <select className="input-lodge" value={purpose} onChange={(e) => setPurpose(e.target.value)} data-testid="doc-upload-purpose">
              {folders.map((f) => <option key={f.id} value={f.purpose}>{f.purpose} → {f.folder_name}</option>)}
            </select>
          </div>
          <div>
            <label className="mb-1 block font-sans text-xs font-semibold uppercase tracking-widest text-stonegrey">File (max 15 MB)</label>
            <input ref={fileRef} type="file" onChange={(e) => setFile(e.target.files[0])}
              className="block w-full font-sans text-sm text-ink/70 file:mr-4 file:rounded-full file:border-0 file:bg-sapphire-deep file:px-4 file:py-2 file:font-semibold file:uppercase file:tracking-widest file:text-parchment hover:file:bg-sapphire-mid"
              data-testid="doc-file-input" />
          </div>
          <button onClick={upload} disabled={!file || uploading} className="btn-sapphire" data-testid="doc-upload-btn">
            {uploading ? <Loader2 size={15} className="animate-spin" /> : <UploadCloud size={15} />} Upload
          </button>
        </div>
      </Card>

      {/* Search & filter */}
      <div className="mb-6 grid gap-4 md:grid-cols-[1fr_auto]">
        <div className="relative">
          <Search size={16} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-ink/40" />
          <input
            className="input-lodge pl-11"
            placeholder="Search by document name, folder or uploader…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="doc-search-input"
          />
        </div>
        <select className="input-lodge md:w-64" value={folderFilter} onChange={(e) => setFolderFilter(e.target.value)} data-testid="doc-folder-filter">
          <option value="all">All folders</option>
          {folders.map((f) => <option key={f.id} value={f.purpose}>{f.purpose}</option>)}
        </select>
      </div>

      {/* Documents grouped by folder */}
      <h2 className="mb-4 font-display text-2xl font-semibold text-sapphire-deep">Filed Documents</h2>
      {(() => {
        const q = search.trim().toLowerCase();
        const matchDoc = (d, f) => !q || [d.filename, d.uploaded_by_name, d.uploaded_by, f.purpose, f.folder_name]
          .filter(Boolean).some((s) => s.toLowerCase().includes(q));
        const groups = folders
          .filter((f) => folderFilter === "all" || f.purpose === folderFilter)
          .map((f) => ({ f, items: docs.filter((d) => d.purpose === f.purpose && matchDoc(d, f)) }))
          .filter((g) => !q || g.items.length > 0);
        if (groups.length === 0) {
          return <Card><p className="font-sans text-sm italic text-ink/50" data-testid="doc-no-results">No documents match your search.</p></Card>;
        }
        return (
          <div className="grid gap-5">
            {groups.map(({ f, items }) => (
              <Card key={f.id} data-testid={`folder-group-${f.id}`}>
                <div className="flex items-center justify-between border-b border-sapphire-deep/10 pb-3">
                  <div className="flex items-center gap-3">
                    <FolderOpen size={20} className="text-gold-deep" />
                    <div>
                      <h3 className="font-display text-lg font-semibold text-sapphire-deep">{f.purpose}</h3>
                      <p className="font-sans text-xs text-ink/50">Drive: {f.folder_name}{f.drive_folder_id ? ` · ${f.drive_folder_id}` : ""}</p>
                    </div>
                  </div>
                  <span className="font-sans text-xs text-ink/50">{items.length} file(s)</span>
                </div>
                {items.length === 0 ? (
                  <p className="pt-4 font-sans text-sm italic text-ink/40">No documents filed here yet.</p>
                ) : (
                  <div className="divide-y divide-sapphire-deep/8">
                    {items.map((d) => (
                      <div key={d.id} className="flex items-center justify-between py-3" data-testid={`doc-item-${d.id}`}>
                        <div className="flex items-center gap-3">
                          <FileText size={18} className="text-sapphire-deep/60" />
                          <div>
                            <p className="font-sans text-sm font-medium text-sapphire-deep">{d.filename}</p>
                            <p className="font-sans text-xs text-ink/50">{bytes(d.size)} · {d.uploaded_by_name || d.uploaded_by} · {fmtDate(d.uploaded_at)}</p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button onClick={() => download(d)} className="rounded-full border border-sapphire-deep/15 p-2 text-sapphire-deep hover:border-gold hover:text-gold-deep" data-testid={`doc-download-${d.id}`}><Download size={14} /></button>
                          <button onClick={() => del(d)} className="rounded-full border border-garnet/30 p-2 text-garnet hover:bg-garnet/10" data-testid={`doc-delete-${d.id}`}><Trash2 size={14} /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            ))}
          </div>
        );
      })()}

      {/* Admin console: folder assignments */}
      {isAdmin && (
        <div className="mt-10" data-testid="folder-admin-panel">
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Settings size={18} className="text-gold-deep" />
              <h2 className="font-display text-2xl font-semibold text-sapphire-deep">Folder Assignment Console</h2>
            </div>
            <button onClick={() => setFolderModal({})} className="btn-sapphire" data-testid="folder-add-btn"><Plus size={15} /> New Assignment</button>
          </div>
          <p className="mb-4 flex items-center gap-2 font-sans text-xs text-ink/50">
            <ShieldCheck size={14} className="text-emerald" /> Restricted to Administrator (Secretary), Treasurer and Worshipful Master.
          </p>
          <Card>
            <div className="divide-y divide-sapphire-deep/8">
              {folders.map((f) => (
                <div key={f.id} className="flex items-center justify-between py-3" data-testid={`folder-row-${f.id}`}>
                  <div>
                    <p className="font-display text-lg text-sapphire-deep">{f.purpose} <span className="font-sans text-sm text-ink/40">→ {f.folder_name}</span></p>
                    <p className="font-sans text-xs text-ink/50">{f.description || "No description"}{f.drive_folder_id ? ` · Drive ID ${f.drive_folder_id}` : " · Drive ID not set"}</p>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => setFolderModal(f)} className="rounded-full border border-sapphire-deep/15 p-2 text-sapphire-deep hover:border-gold hover:text-gold-deep" data-testid={`folder-edit-${f.id}`}><Pencil size={14} /></button>
                    <button onClick={() => delFolder(f)} className="rounded-full border border-garnet/30 p-2 text-garnet hover:bg-garnet/10" data-testid={`folder-delete-${f.id}`}><Trash2 size={14} /></button>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}

      {folderModal !== null && (
        <FolderModal initial={folderModal} onClose={() => setFolderModal(null)} onSaved={() => { setFolderModal(null); loadAll(); }} />
      )}
    </div>
  );
}
